import React from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, ReferenceLine,
} from 'recharts';
import {
  ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent,
} from '@/components/ui/chart';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  KpiCard, ChartCard, ExportMenu, ScreenHeader, EmptyMeter, AXIS_TICK,
} from '@/components/app/kit.jsx';
import { useOps } from '@/hooks/useOps.jsx';
import { daysBetween, fmtDayShort, fmtNum, fmtPct } from '@/lib/format.js';
import { downloadCsv, downloadTxtReport } from '@/lib/export.js';
import { LoadingBody, ErrorBody } from './Overview.jsx';

// AI quality — per provider/hospital observability from raw telemetry (§4):
// mean-confidence trend, low-confidence rate (<0.7), audio minutes processed,
// confidence histogram, and day-level dip flags (a day whose mean confidence
// drops ≥0.08 below the range mean gets flagged).
const LOW_CONF = 0.7;
const DIP_DELTA = 0.08;
const ALL = '__all__';

export default function AiQuality({ range }) {
  const ops = useOps();
  const [provider, setProvider] = React.useState(ALL);
  const [hospital, setHospital] = React.useState(ALL);

  if (ops.loading) return <LoadingBody />;
  if (ops.error) return <ErrorBody error={ops.error} />;

  const tel = ops.telemetry;
  if (!tel.length) return <div className="p-6"><ScreenHeader title="AI quality" /><EmptyMeter sub="Quality metrics appear once ops-telemetry events flow (OPS_* env set on an instance)." /></div>;

  const providers = [...new Set(tel.map((t) => t.provider).filter(Boolean))].sort();
  const hospitals = [...new Set(tel.map((t) => t.hospital).filter(Boolean))].sort();

  const rows = tel.filter((t) =>
    (provider === ALL || t.provider === provider)
    && (hospital === ALL || t.hospital === hospital));

  const confRows = rows.filter((t) => Number.isFinite(Number(t.confidence)));
  const meanConf = confRows.length
    ? confRows.reduce((s, t) => s + Number(t.confidence), 0) / confRows.length : null;
  const lowCount = confRows.filter((t) => Number(t.confidence) < LOW_CONF).length;
  const lowRate = confRows.length ? (lowCount / confRows.length) * 100 : null;
  const audioMinutes = rows.reduce((s, t) => s + (Number(t.audioSeconds) || 0), 0) / 60;

  // Per-day mean confidence + volume.
  const days = daysBetween(range.from, range.to);
  const perDay = days.map((d) => {
    const dayRows = confRows.filter((t) => (t.at || '').slice(0, 10) === d);
    const mean = dayRows.length ? dayRows.reduce((s, t) => s + Number(t.confidence), 0) / dayRows.length : null;
    const low = dayRows.length ? dayRows.filter((t) => Number(t.confidence) < LOW_CONF).length / dayRows.length * 100 : null;
    return { day: d, label: fmtDayShort(d), mean, low, n: dayRows.length };
  });
  const present = perDay.filter((p) => p.mean !== null);
  const rangeMean = present.length ? present.reduce((s, p) => s + p.mean, 0) / present.length : null;
  const dips = present.filter((p) => rangeMean !== null && p.mean <= rangeMean - DIP_DELTA);
  const dipDays = new Set(dips.map((d) => d.day));

  // Confidence histogram (10 buckets).
  const hist = Array.from({ length: 10 }, (_, i) => ({
    bucket: `${(i / 10).toFixed(1)}–${((i + 1) / 10).toFixed(1)}`, count: 0,
    low: (i + 1) / 10 <= LOW_CONF,
  }));
  for (const t of confRows) {
    const b = Math.min(9, Math.floor(Number(t.confidence) * 10));
    hist[b].count += 1;
  }

  const scopeLabel = `${provider === ALL ? 'all providers' : provider} · ${hospital === ALL ? 'all hospitals' : hospital}`;

  const csv = () => downloadCsv(
    `ops-ai-quality-${range.from}_${range.to}.csv`,
    [
      { key: 'day', label: 'Day' }, { key: 'mean', label: 'Mean confidence' },
      { key: 'low', label: 'Low-confidence %' }, { key: 'n', label: 'Scored runs' },
      { key: 'dip', label: 'Dip flag' },
    ],
    perDay.map((p) => ({
      day: p.day, mean: p.mean !== null ? p.mean.toFixed(3) : '',
      low: p.low !== null ? p.low.toFixed(1) : '', n: p.n, dip: dipDays.has(p.day) ? 'DIP' : '',
    })),
  );
  const txt = () => downloadTxtReport(`ops-ai-quality-${range.from}_${range.to}.txt`, {
    title: 'Healther Ops — AI quality',
    meta: [
      `Range: ${range.from} → ${range.to}`, `Scope: ${scopeLabel}`,
      `Mean confidence: ${meanConf !== null ? meanConf.toFixed(3) : '—'}`,
      `Low-confidence rate (<${LOW_CONF}): ${lowRate !== null ? lowRate.toFixed(1) + '%' : '—'}`,
      `Audio minutes processed: ${audioMinutes.toFixed(1)}`,
      `Dip days: ${dips.length ? dips.map((d) => d.day).join(', ') : 'none'}`,
    ],
    sections: [{
      heading: 'Per-day confidence',
      columns: [{ key: 'day', label: 'Day' }, { key: 'mean', label: 'Mean' }, { key: 'low', label: 'Low %' }, { key: 'n', label: 'Runs' }],
      rows: perDay.filter((p) => p.n > 0).map((p) => ({
        day: p.day, mean: p.mean.toFixed(3), low: p.low.toFixed(1) + '%', n: p.n,
      })),
    }],
  });

  return (
    <div className="p-6 cc-enter">
      <ScreenHeader title="AI quality" subtitle={`Confidence & volume from raw telemetry · ${scopeLabel}`}>
        <Select value={provider} onValueChange={setProvider}>
          <SelectTrigger size="sm" className="min-w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All providers</SelectItem>
            {providers.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={hospital} onValueChange={setHospital}>
          <SelectTrigger size="sm" className="min-w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All hospitals</SelectItem>
            {hospitals.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
          </SelectContent>
        </Select>
        <ExportMenu onCsv={csv} onTxt={txt} />
      </ScreenHeader>

      <div className="mb-4 flex flex-wrap gap-3">
        <KpiCard label="Mean confidence" value={meanConf !== null ? meanConf.toFixed(2) : '—'}
          sub={`${fmtNum(confRows.length)} scored runs`} />
        <KpiCard label={`Low-confidence rate (<${LOW_CONF})`}
          tone={lowRate !== null && lowRate > 15 ? 'text-[hsl(var(--allergy))]' : undefined}
          value={lowRate !== null ? fmtPct(lowRate, 1) : '—'} sub={`${fmtNum(lowCount)} runs below threshold`} />
        <KpiCard label="Audio minutes" value={fmtNum(audioMinutes, 1)} sub="processed in range" />
        <KpiCard label="Dip days"
          tone={dips.length ? 'text-[hsl(var(--ai-foreground))]' : undefined}
          value={dips.length}
          sub={dips.length ? `≥${DIP_DELTA} below range mean` : 'confidence steady'} />
      </div>

      {dips.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-[hsl(var(--ai-border))] bg-[hsl(var(--ai-bg))] px-3.5 py-2 text-[12px] font-medium text-[hsl(var(--ai-foreground))]">
          <AlertTriangle className="size-4" /> Confidence dipped on:
          {dips.map((d) => (
            <Badge key={d.day} variant="outline" className="h-5 border-[hsl(var(--ai-border))] px-1.5 text-[10.5px] font-semibold text-[hsl(var(--ai-foreground))] tabular-nums">
              {fmtDayShort(d.day)} · {d.mean.toFixed(2)}
            </Badge>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <ChartCard title="Mean confidence trend" subtitle={`Per day · dashed line = range mean (${rangeMean !== null ? rangeMean.toFixed(2) : '—'})`}
          empty={present.length === 0}>
          <ChartContainer
            config={{
              mean: { label: 'Mean confidence', color: 'hsl(var(--chart-2))' },
              low: { label: 'Low-conf %', color: 'hsl(var(--chart-5))' },
            }}
            className="h-[210px] w-full"
          >
            <LineChart data={perDay} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} tick={AXIS_TICK} minTickGap={24} />
              <YAxis yAxisId="conf" domain={[0, 1]} width={34} tickLine={false} axisLine={false} tick={AXIS_TICK} />
              <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} width={34} tickLine={false} axisLine={false} tick={AXIS_TICK} tickFormatter={(v) => v + '%'} />
              {rangeMean !== null && (
                <ReferenceLine yAxisId="conf" y={rangeMean} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" />
              )}
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              <Line yAxisId="conf" dataKey="mean" type="monotone" stroke="var(--color-mean)" strokeWidth={2}
                dot={(props) => {
                  const { key, cx, cy, payload } = props;
                  if (payload.mean === null) return <g key={key} />;
                  const dip = dipDays.has(payload.day);
                  return <circle key={key} cx={cx} cy={cy} r={dip ? 4 : 2}
                    fill={dip ? 'hsl(var(--allergy))' : 'var(--color-mean)'} />;
                }}
                connectNulls />
              <Line yAxisId="pct" dataKey="low" type="monotone" stroke="var(--color-low)" strokeWidth={1.5} dot={false} connectNulls />
            </LineChart>
          </ChartContainer>
        </ChartCard>

        <ChartCard title="Confidence histogram" subtitle={`Distribution of scored runs · amber = below ${LOW_CONF}`}
          empty={confRows.length === 0}>
          <ChartContainer config={{ count: { label: 'Runs', color: 'hsl(var(--chart-1))' } }} className="h-[210px] w-full">
            <BarChart data={hist} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="bucket" tickLine={false} axisLine={false} tickMargin={8} tick={{ ...AXIS_TICK, fontSize: 10 }} interval={0} angle={-30} height={44} textAnchor="end" />
              <YAxis width={34} tickLine={false} axisLine={false} tick={AXIS_TICK} allowDecimals={false} />
              <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
              <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                {hist.map((h) => (
                  <Cell key={h.bucket} fill={h.low ? 'hsl(var(--ai-foreground))' : 'hsl(var(--chart-1))'} />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        </ChartCard>
      </div>

      <Card className="mt-4 gap-0 py-4">
        <CardHeader className="px-4 pb-0">
          <CardTitle className="text-[13.5px]">Reading this screen</CardTitle>
          <CardDescription className="text-[11.5px]">
            Confidence comes from each run's ops-telemetry event (STT word-confidence, LLM self-score where emitted).
            A "dip day" is a day whose mean lands ≥{DIP_DELTA} below the range mean — check the provider's status page
            and recent deploys on the affected instance first.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4" />
      </Card>
    </div>
  );
}
